import React from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ElfsightWhatsApp from "components/ElfsightWhatsApp";
import LanguageSwitcher from "components/LanguageSwitcher";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import ProtectedRoute from "components/ProtectedRoute";
import SubscriptionRoute from "components/SubscriptionRoute";
import { useAuth } from "contexts/AuthContext";
import { useProfile } from "hooks";
import { getDefaultAuthenticatedPath } from "lib/utils/subscriptionAccess";
import { getCandidateProfilePath } from "lib/utils/candidateRoutes";
import Authentication from './pages/authentication';
import Billing from './pages/billing';
import AIAssistantSettings from './pages/ai-assistant-settings';
import AIAssistantOverview from './pages/ai-assistant/overview';
import AIAssistantCalls from './pages/ai-assistant/calls';
import CandidateAICallsPage from './pages/ai-assistant/candidate';
import MicrosoftOAuthCallback from './pages/integrations/MicrosoftOAuthCallback';
import GoogleOAuthCallback from './pages/integrations/GoogleOAuthCallback';
import CandidateProfile from './pages/candidate-profile';
import Profile from './pages/profile';
import DebugPage from './pages/debug';
import DemoPage from './pages/demo';
import Dashboard from './pages/dashboard';
import CandidatesList from './pages/candidates-list';
import AlertsPage from './pages/alerts';
import ImportCandidates from './pages/import-candidates';
import ConnectATS from './pages/connect-ats';
import FAQ from './pages/faq';
import TermsOfService from './pages/terms';
import PrivacyPolicy from './pages/privacy';
import ForgotPassword from './pages/forgot-password';
import ResetPassword from './pages/reset-password';

const AppRoute = ({ children }) => (
  <ProtectedRoute>
    <SubscriptionRoute>{children}</SubscriptionRoute>
  </ProtectedRoute>
);

const RedirectToDashboard = () => {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const { profile, loading: profileLoading } = useProfile();

  if (loading || (user && profileLoading && !profile)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/authentication" replace />;
  }

  return <Navigate to={getDefaultAuthenticatedPath(profile)} replace />;
};

const LegacyCandidateProfileRoute = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const candidateId = searchParams.get('id') || location.state?.candidateId;

  if (candidateId) {
    return <Navigate to={getCandidateProfilePath(candidateId)} replace />;
  }

  return <Navigate to="/candidates-list" replace />;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <div className="relative">
      <div
        className="absolute right-4 z-40 md:right-6"
        style={{ top: 'calc(1rem + var(--over-limit-banner-height, 0px))' }}
      >
        <LanguageSwitcher />
      </div>
      <ElfsightWhatsApp />
      <RouterRoutes>
        {/* Public routes */}
        <Route path="/" element={<RedirectToDashboard />} />
        <Route path="/authentication" element={<Authentication />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* OAuth return URLs (must be public — opened in popup after n8n redirect) */}
        <Route path="/integrations/microsoft/callback" element={<MicrosoftOAuthCallback />} />
        <Route path="/integrations/google/callback" element={<GoogleOAuthCallback />} />

        {/* Protected routes */}
        <Route path="/dashboard" element={<AppRoute><Dashboard /></AppRoute>} />
        <Route path="/candidates-list" element={<AppRoute><CandidatesList /></AppRoute>} />
        <Route path="/alerts" element={<AppRoute><AlertsPage /></AppRoute>} />
        <Route path="/candidates/:candidateId" element={<AppRoute><CandidateProfile /></AppRoute>} />
        <Route path="/candidate-profile" element={<AppRoute><LegacyCandidateProfileRoute /></AppRoute>} />
        <Route path="/import-candidates" element={<AppRoute><ImportCandidates /></AppRoute>} />
        <Route path="/connect-ats" element={<AppRoute><ConnectATS /></AppRoute>} />
        <Route path="/ai-assistant" element={<Navigate to="/ai-assistant/overview" replace />} />
        <Route path="/ai-assistant/overview" element={<AppRoute><AIAssistantOverview /></AppRoute>} />
        <Route path="/ai-assistant/calls" element={<AppRoute><AIAssistantCalls /></AppRoute>} />
        <Route path="/ai-assistant/candidate/:candidateId" element={<AppRoute><CandidateAICallsPage /></AppRoute>} />
        <Route path="/ai-assistant/setup" element={<AppRoute><AIAssistantSettings /></AppRoute>} />
        <Route path="/ai-assistant-settings" element={<Navigate to="/ai-assistant/setup" replace />} />
        <Route path="/profile" element={<AppRoute><Profile /></AppRoute>} />
        <Route path="/debug" element={<AppRoute><DebugPage /></AppRoute>} />
        <Route path="/demo" element={<AppRoute><DemoPage /></AppRoute>} />
        <Route path="/billing" element={<AppRoute><Billing /></AppRoute>} />
        <Route path="/faq" element={<AppRoute><FAQ /></AppRoute>} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </div>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
